import React, { useState, useRef, useEffect } from "react";
import { ToggleSwitch } from "./ToggleSwitch";

interface ColorPickerProps {
  value: string; // hex, rgb, rgba, or hsl
  onChange: (color: string) => void;
  className?: string;
  style?: React.CSSProperties;
  // Outline control
  showOutlineToggle?: boolean;
  outlineEnabled?: boolean;
  onOutlineChange?: (enabled: boolean) => void;
  outlineColor?: string; // Outline color (or "auto" for automatic)
  onOutlineColorChange?: (color: string) => void;
}

// Predefined color swatches for line color (8 swatches)
const LINE_COLOR_SWATCHES = {
  "basic:white": "#ffffff",
  "basic:black": "#000000",
  "bikedash:blue": "#3e6ae1",
  "material:red": "#dc2626",
  "material:green": "#22c55e",
  "material:purple": "#9333ea",
  "material:orange": "#ea580c",
  "material:cyan": "#0891b2",
};

// Predefined swatches for outline color (auto + same 8 colors)
const OUTLINE_COLOR_SWATCHES = {
  auto: "auto", // Automatic based on brightness
  ...LINE_COLOR_SWATCHES,
};

// Convert various color formats to RGBA
function parseColor(color: string): {
  r: number;
  g: number;
  b: number;
  a: number;
} {
  // Default to blue if parsing fails
  const defaultColor = { r: 62, g: 106, b: 225, a: 1 };

  if (!color) return defaultColor;

  // Handle hex colors
  if (color.startsWith("#")) {
    const hex = color.slice(1);
    if (hex.length === 3) {
      const r = parseInt(hex[0] + hex[0], 16);
      const g = parseInt(hex[1] + hex[1], 16);
      const b = parseInt(hex[2] + hex[2], 16);
      return { r, g, b, a: 1 };
    } else if (hex.length === 6) {
      const r = parseInt(hex.slice(0, 2), 16);
      const g = parseInt(hex.slice(2, 4), 16);
      const b = parseInt(hex.slice(4, 6), 16);
      return { r, g, b, a: 1 };
    }
  }

  // Handle rgb/rgba
  if (color.startsWith("rgb")) {
    const match = color.match(/rgba?\(([^)]+)\)/);
    if (match) {
      const parts = match[1].split(",").map((p) => parseFloat(p.trim()));
      return {
        r: parts[0] || 0,
        g: parts[1] || 0,
        b: parts[2] || 0,
        a: parts[3] !== undefined ? parts[3] : 1,
      };
    }
  }

  return defaultColor;
}

// Convert RGBA to various formats
function rgbaToHex(r: number, g: number, b: number): string {
  const toHex = (n: number) =>
    Math.round(Math.max(0, Math.min(255, n)))
      .toString(16)
      .padStart(2, "0");
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function rgbaToString(r: number, g: number, b: number, a: number): string {
  if (a === 1) {
    return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`;
  }
  return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a.toFixed(2)})`;
}

export function ColorPicker({
  value,
  onChange,
  className = "",
  style = {},
  showOutlineToggle = false,
  outlineEnabled = true,
  onOutlineChange,
  outlineColor = "auto",
  onOutlineColorChange,
}: ColorPickerProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"line" | "outline">("line");
  const [color, setColor] = useState(() => parseColor(value));
  const pickerRef = useRef<HTMLDivElement>(null);
  const lineColorInputRef = useRef<HTMLInputElement>(null);
  const outlineColorInputRef = useRef<HTMLInputElement>(null);

  // Update internal state when prop changes
  useEffect(() => {
    setColor(parseColor(value));
  }, [value]);

  // Close picker when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }

    return undefined;
  }, [isOpen]);

  const handleColorChange = (newColor: typeof color) => {
    setColor(newColor);
    onChange(rgbaToString(newColor.r, newColor.g, newColor.b, newColor.a));
  };

  const handleLineSwatchSelect = (swatchColor: string) => {
    const parsed = parseColor(swatchColor);
    handleColorChange({ ...parsed, a: color.a }); // Keep current alpha
  };

  const handleOutlineSwatchSelect = (swatchColor: string) => {
    if (onOutlineColorChange) {
      onOutlineColorChange(swatchColor);
    }
  };

  const currentColorStyle = {
    backgroundColor: rgbaToString(color.r, color.g, color.b, color.a),
  };

  return (
    <div
      className={`color-picker ${className}`}
      style={{ position: "relative", ...style }}
    >
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: "40px",
          height: "40px",
          border: "1px solid var(--border)",
          borderRadius: "6px",
          cursor: "pointer",
          background: "transparent",
          padding: "2px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "4px",
            // Black and white checkerboard background
            backgroundImage: `
              linear-gradient(${rgbaToString(color.r, color.g, color.b, color.a)}, ${rgbaToString(color.r, color.g, color.b, color.a)}),
              repeating-conic-gradient(#000 0% 25%, #fff 0% 50%)
            `,
            backgroundSize: "auto, 8px 8px",
          }}
        />
      </button>

      {isOpen && (
        <div
          ref={pickerRef}
          style={{
            position: "absolute",
            top: "45px",
            right: "0",
            background: "var(--bg-elevated)",
            border: "1px solid var(--border)",
            borderRadius: "8px",
            padding: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
            zIndex: 1000,
            minWidth: "220px",
          }}
        >
          {/* Tabs */}
          {showOutlineToggle && (
            <div
              style={{
                display: "flex",
                marginBottom: "12px",
                borderBottom: "1px solid var(--border)",
              }}
            >
              <button
                onClick={() => setActiveTab("line")}
                style={{
                  flex: 1,
                  padding: "6px 12px",
                  background: "transparent",
                  border: "none",
                  borderBottom:
                    activeTab === "line"
                      ? "2px solid var(--accent)"
                      : "2px solid transparent",
                  color:
                    activeTab === "line"
                      ? "var(--accent)"
                      : "var(--text-secondary)",
                  cursor: "pointer",
                  fontSize: "11px",
                }}
              >
                Line
              </button>
              <button
                onClick={() => setActiveTab("outline")}
                style={{
                  flex: 1,
                  padding: "6px 12px",
                  background: "transparent",
                  border: "none",
                  borderBottom:
                    activeTab === "outline"
                      ? "2px solid var(--accent)"
                      : "2px solid transparent",
                  color:
                    activeTab === "outline"
                      ? "var(--accent)"
                      : "var(--text-secondary)",
                  cursor: "pointer",
                  fontSize: "11px",
                }}
              >
                Outline
              </button>
            </div>
          )}

          {/* Line Color Tab */}
          {activeTab === "line" && (
            <div>
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "var(--text-primary)",
                  marginBottom: "8px",
                }}
              >
                Line Color
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(4, 1fr)",
                  gap: "6px",
                  marginBottom: "12px",
                }}
              >
                {Object.entries(LINE_COLOR_SWATCHES).map(
                  ([name, swatchColor]) => {
                    const isSelected =
                      value.toLowerCase() === swatchColor.toLowerCase();
                    return (
                      <button
                        key={name}
                        onClick={() => handleLineSwatchSelect(swatchColor)}
                        style={{
                          width: "32px",
                          height: "32px",
                          backgroundColor: swatchColor,
                          border: isSelected
                            ? "3px solid var(--accent)"
                            : "2px solid var(--border)",
                          borderRadius: "4px",
                          cursor: "pointer",
                          boxShadow: isSelected
                            ? "0 0 0 1px var(--accent)"
                            : "none",
                        }}
                        title={name.replace(":", " ")}
                      />
                    );
                  },
                )}
                {/* Custom color swatch */}
                <button
                  onClick={() => lineColorInputRef.current?.click()}
                  style={{
                    width: "32px",
                    height: "32px",
                    border: !Object.values(LINE_COLOR_SWATCHES).some(
                      (c) => c.toLowerCase() === value.toLowerCase(),
                    )
                      ? "3px solid var(--accent)"
                      : "2px solid var(--border)",
                    borderRadius: "4px",
                    cursor: "pointer",
                    background:
                      "linear-gradient(135deg, #ff0000 0%, #00ff00 50%, #0000ff 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10px",
                    fontWeight: 600,
                    color: "#fff",
                    textShadow: "0 0 2px #000",
                    boxShadow: !Object.values(LINE_COLOR_SWATCHES).some(
                      (c) => c.toLowerCase() === value.toLowerCase(),
                    )
                      ? "0 0 0 1px var(--accent)"
                      : "none",
                  }}
                  title="Custom color"
                >
                  +
                </button>
                <input
                  ref={lineColorInputRef}
                  type="color"
                  value={rgbaToHex(color.r, color.g, color.b)}
                  onChange={(e) => handleLineSwatchSelect(e.target.value)}
                  style={{ display: "none" }}
                />
              </div>

              {/* Opacity Slider */}
              <div>
                <label
                  style={{
                    fontSize: "11px",
                    color: "var(--text-secondary)",
                    display: "block",
                    marginBottom: "4px",
                  }}
                >
                  Opacity: {Math.round(color.a * 100)}%
                </label>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={color.a}
                  onChange={(e) =>
                    handleColorChange({ ...color, a: Number(e.target.value) })
                  }
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          )}

          {/* Outline Color Tab */}
          {activeTab === "outline" && showOutlineToggle && (
            <div>
              <div
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  color: "var(--text-primary)",
                  marginBottom: "8px",
                }}
              >
                Outline Color
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, 1fr)",
                  gap: "6px",
                  marginBottom: "12px",
                }}
              >
                {Object.entries(OUTLINE_COLOR_SWATCHES).map(([name, value]) => {
                  const isSelected = outlineColor === value;
                  return (
                    <button
                      key={name}
                      onClick={() => {
                        handleOutlineSwatchSelect(value);
                      }}
                      style={{
                        width: "32px",
                        height: "32px",
                        backgroundColor:
                          value === "auto" ? "transparent" : value,
                        border: isSelected
                          ? "3px solid var(--accent)"
                          : "2px solid var(--border)",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "9px",
                        fontWeight: isSelected ? 600 : 500,
                        color:
                          value === "#000000"
                            ? "#ffffff"
                            : value === "#ffffff"
                              ? "#000000"
                              : value === "auto"
                                ? isSelected
                                  ? "var(--accent)"
                                  : "var(--text-primary)"
                                : "transparent",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        boxShadow: isSelected
                          ? "0 0 0 1px var(--accent)"
                          : "none",
                      }}
                      title={value === "auto" ? "Auto" : name.replace(":", " ")}
                    >
                      {value === "auto" ? "A" : ""}
                    </button>
                  );
                })}
                {/* Custom outline color swatch */}
                <button
                  onClick={() => {
                    outlineColorInputRef.current?.click();
                  }}
                  style={{
                    width: "32px",
                    height: "32px",
                    border:
                      outlineColor !== "auto" &&
                      !Object.values(OUTLINE_COLOR_SWATCHES).includes(
                        outlineColor,
                      )
                        ? "3px solid var(--accent)"
                        : "2px solid var(--border)",
                    borderRadius: "4px",
                    cursor: "pointer",
                    background:
                      "linear-gradient(135deg, #ff0000 0%, #00ff00 50%, #0000ff 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "10px",
                    fontWeight: 600,
                    color: "#fff",
                    textShadow: "0 0 2px #000",
                    boxShadow:
                      outlineColor !== "auto" &&
                      !Object.values(OUTLINE_COLOR_SWATCHES).includes(
                        outlineColor,
                      )
                        ? "0 0 0 1px var(--accent)"
                        : "none",
                  }}
                  title="Custom outline color"
                >
                  +
                </button>
                <input
                  ref={outlineColorInputRef}
                  type="color"
                  value={
                    outlineColor === "auto"
                      ? "#ffffff"
                      : outlineColor?.startsWith("#")
                        ? outlineColor
                        : "#ffffff"
                  }
                  onChange={(e) => handleOutlineSwatchSelect(e.target.value)}
                  style={{ display: "none" }}
                />
              </div>
            </div>
          )}

          {/* Color preview */}
          {activeTab === "line" && (
            <>
              <div
                style={{
                  height: "20px",
                  borderRadius: "4px",
                  border: "1px solid var(--border)",
                  backgroundImage: `
                    linear-gradient(${rgbaToString(color.r, color.g, color.b, color.a)}, ${rgbaToString(color.r, color.g, color.b, color.a)}),
                    repeating-conic-gradient(#000 0% 25%, #fff 0% 50%)
                  `,
                  backgroundSize: "auto, 8px 8px",
                }}
              />
              <div
                style={{
                  marginTop: "8px",
                  fontSize: "10px",
                  color: "var(--text-secondary)",
                  fontFamily: "monospace",
                }}
              >
                {rgbaToString(color.r, color.g, color.b, color.a)}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
